import { formatMoney } from '@theme/money-formatting';
import { StandardEvents, CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

class VivaCompleteRoutine extends HTMLElement {
  #abortController = new AbortController();

  connectedCallback() {
    this.#abortController = new AbortController();
    document.addEventListener(StandardEvents.productSelect, this.#variantChanged, { signal: this.#abortController.signal });
    document.addEventListener('quantity-selector:update', this.#quantityChanged, { signal: this.#abortController.signal });
    this.addEventListener('change', this.#updateTotal, { signal: this.#abortController.signal });
    this.querySelector('[data-routine-submit]')?.addEventListener('click', this.#addRoutine, {
      signal: this.#abortController.signal,
    });
    const selected = document.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"] script[type="application/json"]`);
    if (selected) {
      try { this.#applyVariant(JSON.parse(selected.textContent)); } catch { this.dataset.baseAvailable = 'false'; }
    }
    this.#updateTotal();
    const button = this.querySelector('[data-routine-submit]');
    if (button) button.disabled = this.dataset.baseAvailable !== 'true';
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  #formatMoney = (cents) => {
    const currency = window.Shopify?.currency?.active || 'EUR';
    const formatted = formatMoney(cents, this.dataset.moneyFormat || '{{amount_with_comma_separator}}', currency);
    const parser = new DOMParser().parseFromString(formatted, 'text/html');
    return parser.body.textContent;
  };

  #quantity = () => {
    const form = document.querySelector(`product-form-component[data-product-id="${this.dataset.productId}"]`);
    const input = form?.querySelector('input[name="quantity"]');
    return Math.max(1, Number(input?.value || this.dataset.baseQuantity || 1));
  };

  #quantityChanged = (event) => {
    if (event.target instanceof Element && event.target.closest(`product-form-component[data-product-id="${this.dataset.productId}"]`)) this.#updateTotal();
  };

  #applyVariant = (variant) => {
    if (!variant?.id || !Number.isFinite(Number(variant.price))) {
      this.dataset.baseAvailable = 'false';
      return;
    }
    this.dataset.baseVariantId = String(variant.id);
    this.dataset.basePrice = String(variant.price);
    this.dataset.baseAvailable = String(variant.available);
    const basePrice = this.querySelector('[data-routine-base-price]');
    if (basePrice) basePrice.textContent = this.#formatMoney(Number(variant.price));
  };

  #variantChanged = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    const button = this.querySelector('[data-routine-submit]');
    if (button) button.disabled = true;
    event.promise?.then(({ detail }) => {
      if (!this.isConnected) return;
      if (detail?.productId && String(detail.productId) !== this.dataset.productId) return;
      const fresh = detail?.html?.getElementById(this.id);
      if (detail?.resource?.id) {
        this.#applyVariant(detail.resource);
      } else if (fresh) {
        for (const key of ['basePrice', 'baseVariantId', 'baseAvailable', 'baseQuantity']) this.dataset[key] = fresh.dataset[key];
        const basePrice = this.querySelector('[data-routine-base-price]');
        if (basePrice) basePrice.textContent = this.#formatMoney(Number(this.dataset.basePrice));

      } else {
        this.dataset.baseAvailable = 'false';
      }
      if (button) button.disabled = this.dataset.baseAvailable !== 'true';
      this.#updateTotal();
    }).catch(() => {
      this.dataset.baseAvailable = 'false';
      this.#setStatus(this.dataset.errorText, true);
    });
  };

  #selectedItems = () => {
    const mainVariantInput = document.querySelector(
      `product-form-component[data-product-id="${this.dataset.productId}"] input[name="id"]`
    );
    const baseVariantId = mainVariantInput?.value || this.dataset.baseVariantId;
    const items = [];

    if (baseVariantId) items.push({ id: Number(baseVariantId), quantity: this.#quantity() });
    for (const input of this.querySelectorAll('[data-routine-checkbox]:checked')) {
      items.push({ id: Number(input.dataset.variantId), quantity: 1 });
    }

    return items;
  };

  #updateTotal = () => {
    let total = Number(this.dataset.basePrice || 0) * this.#quantity();
    const selectedInputs = this.querySelectorAll('[data-routine-checkbox]:checked');
    for (const input of selectedInputs) {
      total += Number(input.dataset.price || 0);
    }

    const totalNode = this.querySelector('[data-routine-total]');
    if (totalNode) {
      totalNode.textContent = this.#formatMoney(total);
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        totalNode.animate(
          [
            { opacity: 0.45, transform: 'translateY(4px)' },
            { opacity: 1, transform: 'translateY(0)' },
          ],
          { duration: 240, easing: 'cubic-bezier(.22,1,.36,1)' }
        );
      }
    }

    const itemCount = selectedInputs.length + this.#quantity();
    const countNode = this.querySelector('[data-routine-count]');
    if (countNode) {
      const label = itemCount === 1 ? this.dataset.itemSingular : this.dataset.itemPlural;
      countNode.textContent = `${itemCount} ${label || ''}`.trim();
    }
  };

  #setStatus(message, isError = false) {
    const status = this.querySelector('[data-routine-status]');
    if (!status) return;
    status.textContent = message;
    status.classList.toggle('vp-routine-status--error', isError);
  }

  #addRoutine = async () => {
    const button = this.querySelector('[data-routine-submit]');
    const items = this.#selectedItems();
    if (!(button instanceof HTMLButtonElement) || items.length === 0 || button.disabled || this.dataset.baseAvailable !== 'true') return;

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((component) => component instanceof HTMLElement ? component.dataset.sectionId : null)
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      promise: deferred.promise,
    }));

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    this.dataset.state = 'adding';
    this.#setStatus(this.dataset.addingText || 'Adding…');

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'Add to cart failed');

      const cartResponse = await fetch(`${Theme.routes.cart_url}.json`, { headers: { Accept: 'application/json' } });
      const cart = await cartResponse.json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'viva-complete-routine',
          sourceId: this.id,
          itemCount: items.length,
          productId: this.dataset.productId,
          sections: result.sections,
          didError: false,
        },
      });
      this.dataset.state = 'added';
      this.#setStatus(this.dataset.addedText || 'Added to cart');
    } catch (error) {
      deferred.reject(error);
      const message = error instanceof Error ? error.message : (this.dataset.errorText || 'Please try again.');
      this.dataset.state = 'error';
      this.#setStatus(message, true);
      this.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
    } finally {
      button.disabled = this.dataset.baseAvailable !== 'true';
      button.removeAttribute('aria-busy');
      window.setTimeout(() => {
        if (this.dataset.state !== 'adding') delete this.dataset.state;
      }, 1800);
    }
  };
}

if (!customElements.get('viva-complete-routine')) {
  customElements.define('viva-complete-routine', VivaCompleteRoutine);
}
